import axios from 'axios';
import { post } from 'services/client';
import { apiEndpoints } from 'config';

/**
 * Create a batch for multi-file upload (notes, enhancementOption). Returns { batchId }.
 */
export const createBatch = async (payload = {}) => {
  const { data } = await post({
    url: apiEndpoints.partnerUploadsBatches,
    data: {
      notes: payload.notes ?? null,
      enhancementOption: payload.enhancementOption ?? null,
    },
  });
  return data;
};

/**
 * Initiate upload for one file. Returns { uploadId, presignedUrl?, expiresIn?, s3Key, s3Bucket } for small files
 * or { uploadId, multipartUploadId, s3Key, s3Bucket, partSize, totalParts } for large files.
 */
export const initiateUpload = async (file, serviceProviderId, partnerUploadBatchId = null) => {
  const { data } = await post({
    url: apiEndpoints.partnerUploadsInitiate,
    data: {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/octet-stream',
      serviceProviderId,
      ...(partnerUploadBatchId != null && { partnerUploadBatchId }),
    },
  });
  return data;
};

/**
 * Upload file directly to S3 using pre-signed PUT URL. onProgress(percent) optional.
 */
export const uploadToS3 = async (file, presignedUrl, onProgress) => {
  const contentType = file.type || 'application/octet-stream';
  const config = {
    method: 'PUT',
    url: presignedUrl,
    data: file,
    headers: {
      'Content-Type': contentType,
    },
  };
  if (onProgress && typeof XMLHttpRequest !== 'undefined') {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      });
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  }
  await axios(config);
};

/**
 * Complete a standard (single PUT) upload.
 */
export const completeUpload = async uploadId => {
  const { data } = await post({
    url: apiEndpoints.partnerUploadsComplete,
    data: { uploadId },
  });
  return data;
};

/**
 * Get pre-signed URL for one part of a multipart upload.
 */
export const getMultipartPartUrl = async (uploadId, partNumber) => {
  const { data } = await post({
    url: apiEndpoints.partnerUploadsMultipartPartUrl,
    data: { uploadId, partNumber },
  });
  return data;
};

/**
 * Upload one part to S3 (PUT to presigned part URL). Returns ETag from response headers.
 */
export const uploadPartToS3 = async (partBody, presignedUrl) => {
  const response = await axios.put(presignedUrl, partBody, {
    headers: { 'Content-Type': 'application/octet-stream' },
    validateStatus: () => true,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Part upload failed: ${response.status}`);
  }
  const etag = response.headers?.etag ?? response.headers?.ETag;
  if (!etag) throw new Error('Missing ETag in part response');
  return etag.replace(/"/g, '');
};

/**
 * Complete multipart upload. parts: [{ partNumber, etag }]. Server sets partsUploaded from parts.length.
 */
export const completeMultipartUpload = async (uploadId, parts) => {
  const { data } = await post({
    url: apiEndpoints.partnerUploadsMultipartComplete,
    data: { uploadId, parts },
  });
  return data;
};

/**
 * Full flow: initiate -> upload (single PUT or multipart) -> complete.
 * For files >= 50MB uses multipart (part size from server); for smaller files uses single PUT.
 * onProgress(percent, fileName) called during upload.
 */
export const uploadOneFile = async (file, serviceProviderId, partnerUploadBatchId, onProgress) => {
  const init = await initiateUpload(file, serviceProviderId, partnerUploadBatchId);
  const { uploadId, presignedUrl, partSize, totalParts } = init;

  if (presignedUrl) {
    await uploadToS3(file, presignedUrl, p => onProgress?.(p, file.name));
    return completeUpload(uploadId);
  }

  if (totalParts != null && totalParts > 0 && partSize > 0) {
    const parts = [];
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const partBody = file.slice(start, end);
      const { presignedUrl: partUrl } = await getMultipartPartUrl(uploadId, partNumber);
      const etag = await uploadPartToS3(partBody, partUrl);
      parts.push({ partNumber, etag });
      onProgress?.(Math.round((partNumber / totalParts) * 100), file.name);
    }
    return completeMultipartUpload(uploadId, parts);
  }

  throw new Error('Invalid initiate response');
};

export default {
  createBatch,
  initiateUpload,
  uploadToS3,
  completeUpload,
  getMultipartPartUrl,
  uploadPartToS3,
  completeMultipartUpload,
  uploadOneFile,
};
